import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback used while loading or on fetch errors — everything visible.
const FALLBACK_MODULES = {
  client_information:    { global: true, admin: true, manager: true, technician: true },
  job_information:       { global: true, admin: true, manager: true, technician: true },
  job_details:           { global: true, admin: true, manager: true, technician: true },
  proposals:             { global: true, admin: true, manager: true, technician: true },
  assign_team_members:   { global: true, admin: true, manager: true, technician: true },
  job_photos:            { global: true, admin: true, manager: true, technician: true },
  job_811_tickets:       { global: true, admin: true, manager: true, technician: true },
  job_building_permits:  { global: true, admin: true, manager: true, technician: true },
  field_reports:         { global: true, admin: true, manager: true, technician: true },
};

// Map a user role string to one of our 3 layout buckets.
// super_user is treated as admin.
function bucketForRole(role) {
  if (role === 'super_user' || role === 'admin') return 'admin';
  if (role === 'manager') return 'manager';
  return 'technician';
}

/**
 * Hook returning a `canShowModule(key)` predicate that respects:
 *   1. Global master toggle
 *   2. Per-role toggle (admin / manager / technician)
 *
 * Data loads asynchronously. While loading, all modules are visible (fail-open).
 */
export function useJobLayoutSettings(role) {
  const [modules, setModules] = useState(FALLBACK_MODULES);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/job-layout-settings`)
      .then((r) => {
        if (!alive) return;
        if (r.data?.modules) setModules(r.data.modules);
        setLoaded(true);
      })
      .catch(() => {
        if (alive) setLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  const bucket = bucketForRole(role);

  const canShowModule = useCallback(
    (moduleKey) => {
      const m = modules[moduleKey];
      if (!m) return true; // unknown module → visible
      if (m.global === false) return false;
      return m[bucket] !== false;
    },
    [modules, bucket]
  );

  return { canShowModule, modules, loaded };
}
